import React, { useCallback, useEffect, useRef, useState } from "react";
import { IJourneyLevel } from "./types";
import { useJourneyData } from "./useJourneyData";
import JourneyCard from "./JourneyCard";
import StoryModal from "./StoryModal";
import MagicBackpackModal from "./MagicBackpackModal";
import LottieWebPlayer from "./LottieWebPlayer";
import styles from "./styles";
import BackgroundMusicButton from "../../components/BackgroundMusic/BackgroundMusicButton.web";
import { useNavigate } from "react-router-dom";
import { WebLoader } from "../../components/Loader";
import { clearStoryData } from "../../store/storySlice";
import { useAppDispatch } from "../../store/hooks";
import { webRoutes } from "../../router/webRoutes";
import "./WebAppJourneyScreen.css";
import appSwitchIcon from "../../appAssets/jsons/appSwitchIcon.json";
import Lottie from "lottie-react";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";

// Assets
const journeySnowfloorImg = require("./assets/images/journeySnowfloor.png");
const zigzagLeftImg = require("./assets/images/zigzagLeft.png");
const zigzagRightImg = require("./assets/images/zigzagRight.png");
const dottedLeftImg = require("./assets/images/dottedLeft.png");
const dottedRightImg = require("./assets/images/dottedright.png");
const bouncyBagJson = require("./assets/jsons/bouncyBag.json");
const homeIconJson = require("./assets/jsons/homeIcon.json");
const yetiHappyDanceJson = require("./assets/jsons/YetiHappyDance.json");

// All px values in this file were authored for this viewport width.
const DESIGN_WIDTH = 1440;

const LEVEL_HEIGHT = 500;
const NODE_LEFT = "35%";
const NODE_RIGHT = "67%";

interface WebMyJourneyScreenProps {
  onNavigateHome?: () => void;
  onNavigateToStory?: (storyId: string) => void;
  onNavigateToMagicBackpack?: (
    contentType: "Video" | "Audio",
    navigatedFrom: string,
    apiUrl: string,
  ) => void;
}

const WebMyJourneyScreen: React.FC<WebMyJourneyScreenProps> = ({
  onNavigateHome,
  onNavigateToStory,
  onNavigateToMagicBackpack,
}) => {
  const [journeyLevels, setJourneyLevels] = useState<IJourneyLevel[]>([]);
  const [selectedStory, setSelectedStory] = useState<IJourneyLevel | null>(
    null,
  );
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showBackpackModal, setShowBackpackModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  // Shrink below DESIGN_WIDTH, never enlarge above 1.
  const [viewportScale, setViewportScale] = useState(() =>
    Math.min(window.innerWidth / DESIGN_WIDTH, 1),
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { fetchJourneyLevels, extractStoryId } = useJourneyData();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Block browser back / forward buttons while on the Rooms screen.
  // pushState adds a sentinel entry so the first Back press lands here;
  // the popstate handler immediately re-pushes to neutralise every subsequent press.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const compute = () =>
      setViewportScale(Math.min(window.innerWidth / DESIGN_WIDTH, 1));
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const loadJourneyData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const levels = await fetchJourneyLevels("?journeyPage=true");
      setJourneyLevels(levels ?? []);
    } catch {
      setError("Failed to load journey. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchJourneyLevels]);

  useEffect(() => {
    loadJourneyData();
  }, [loadJourneyData]);

  useEffect(() => {
    if (journeyLevels.length > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [journeyLevels]);

  const isCurrent = (level: IJourneyLevel, index: number): boolean => {
    const s = level.status?.toLowerCase();
    return (
      (index === 0 &&
        s !== "restarted" &&
        s !== "completed" &&
        s !== "finished") ||
      s === "pending" ||
      s === "started"
    );
  };

  const getZigzagImage = (i: number, isEven: boolean): string => {
    if (i === 0) return isEven ? dottedLeftImg : dottedRightImg;
    return isEven ? zigzagRightImg : zigzagLeftImg;
  };

  const handleCardClick = (level: IJourneyLevel) => {
    trackEvent(TrackingEvents.story_initiated, {
      from: "my journey",
      story_name: level.name,
    });
    setSelectedStory(level);
    setShowStoryModal(true);
  };

  const handleStoryStart = () => {
    dispatch(clearStoryData());
    if (!selectedStory) return;
    trackEvent(TrackingEvents.story_started, {
      story_name: selectedStory.name,
    });
    setShowStoryModal(false);
    const storyId = extractStoryId(selectedStory.url);
    navigate("/web/WebStoryScreen", { state: { storyId } });
  };

  const handleBackpackContentPress = (contentType: "Video" | "Audio") => {
    setShowBackpackModal(false);
    const apiUrl = `retrieveMagicBackpack/${contentType}`;
    console.log(apiUrl, "apiUrlapiUrlapiUrl");
    navigate(webRoutes.magicBackpack, {
      state: { contentType, navigatedFrom: "journeyscreen", apiUrl },
    });
  };

  const handleHomePress = () => {
    trackEvent(TrackingEvents.home_icon_clicked);
    navigate("/web/rooms");
  };

  // All values at 1:1 scale — css zoom on the container handles shrinking.
  const totalHeight = Math.max(journeyLevels.length * LEVEL_HEIGHT + 100, 600);

  const renderLevels = () =>
    journeyLevels.map((level, i) => {
      const isEven = i % 2 !== 0;
      const current = isCurrent(level, i);
      const zigzag = getZigzagImage(i, isEven);
      const topOffset = i * LEVEL_HEIGHT;
      // const nodeCenterLeft = isEven ? NODE_RIGHT : NODE_LEFT;
      const nodeCenterLeft = isEven ? "84.5%" : "38.5%";

      // For the first level, extend the path upward to fill the paddingTop gap
      // so the dotted trail reaches the top of the screen behind the sticky header.
      const topBleed = i === 0 ? 70 : 0;
      const pathStyle: React.CSSProperties = isEven
        ? {
            position: "absolute",
            top: -topBleed,
            left: 0,
            width: "95%",
            height: topBleed > 0 ? `calc(100% + ${topBleed}px)` : "100%",
            backgroundImage: `url(${zigzag})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right center",
            zIndex: 0,
          }
        : {
            position: "absolute",
            // top: -topBleed,
            right: 0,
            width: "72%",
            height: topBleed > 0 ? `calc(100%)` : "100%",
            backgroundImage: `url(${zigzag})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
            zIndex: 0,
          };

      return (
        <div
          key={`level-${i}`}
          style={{
            position: "absolute",
            top: topOffset,
            left: 0,
            right: 0,
            height: LEVEL_HEIGHT,
            zIndex: 1,
          }}
        >
          <div style={pathStyle} />

          <div
            style={{
              position: "absolute",
              bottom: 80,
              left: nodeCenterLeft,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              zIndex: 2,
            }}
          >
            {current && (
              <div
                style={{
                  position: "absolute",
                  right: "100%",
                  marginRight: 6,
                  pointerEvents: "none",
                }}
              >
                <LottieWebPlayer
                  source={yetiHappyDanceJson}
                  style={{ width: 270, height: 270 }}
                  autoPlay
                  loop
                />
              </div>
            )}

            <JourneyCard
              level={level}
              isCurrent={current}
              onClick={() => handleCardClick(level)}
            />
            <div
              style={{
                width: 142,
                height: 42,
                backgroundColor: "#1D262D",
                borderRadius: "50%",
                opacity: 0.2,
                marginTop: -10,
                zIndex: -1,
                // boxShadow: "0 6px 14px rgba(0,0,0,0.38)",
              }}
            />
          </div>
        </div>
      );
    });

  return (
    // Outer div is the scroll container so the scrollbar is at the viewport's right edge.
    <div
      ref={scrollContainerRef}
      style={{
        ...styles.screenWrapper,
        backgroundImage: `url(${journeySnowfloorImg})`,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <header
        className="web-convo-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          className="rooms__ctrl-btn"
          onClick={handleHomePress}
          aria-label="Go home"
        >
          <LottieWebPlayer
            source={homeIconJson}
            style={styles.homeIconLottie}
            autoPlay={false}
            loop={false}
          />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="rooms__ctrl-btn">
            <BackgroundMusicButton
              onPlayStateChange={(isPlaying: boolean) =>
                setMusicPlaying(isPlaying)
              }
            />
          </div>
          <button
            className="rooms__ctrl-btn"
            aria-label="Go to Dashboard"
            onClick={() => {
              localStorage.setItem("selectedMode", "admin");
              navigate("/dashboard");
            }}
          >
            <Lottie
              animationData={appSwitchIcon}
              loop={false}
              autoplay={false}
              className="rooms__ctrl-lottie"
            />
          </button>
        </div>
      </header>

      {!showBackpackModal && (
        <button
          style={{ ...styles.backpackBtn, position: "fixed", left: 0 }}
          onClick={() => setShowBackpackModal(true)}
          aria-label="Open magic backpack"
        >
          <LottieWebPlayer
            source={bouncyBagJson}
            style={styles.backpackLottie}
            autoPlay={false}
            loop={false}
          />
        </button>
      )}

      {/*
        css `zoom` scales both layout and visual uniformly — unlike transform:scale which
        only affects visual rendering. This means:
        - percentages (NODE_LEFT/RIGHT, path widths) and px values all shrink together
        - the outer scroll container sees the correct shrunken height → scrollbar is accurate
        - no manual per-value scaling math needed
      */}
      <div
        style={{
          width: DESIGN_WIDTH,
          // @ts-ignore — zoom is valid CSS, TS types don't include it
          zoom: viewportScale,
          margin: "0 auto",
          // paddingTop: 70,
          // paddingBottom: 40,
          // paddingLeft: 70,
          // background: "red",
        }}
      >
        <div
          style={{ position: "relative", width: "100%", height: totalHeight }}
        >
          {renderLevels()}
        </div>
      </div>

      {/* {isLoading && <WebLoader fullScreen />} */}

      {error && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(255,255,255,0.9)",
            borderRadius: 12,
            padding: "16px 24px",
            textAlign: "center",
            zIndex: 20,
          }}
        >
          <p style={{ color: "#c0392b", fontWeight: 600, marginBottom: 12 }}>
            {error}
          </p>
          <button
            onClick={loadJourneyData}
            style={{ ...styles.continueBtn, padding: "8px 20px", fontSize: 14 }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Modals rendered outside the zoom div so they cover the full viewport */}
      {showStoryModal && selectedStory && (
        <StoryModal
          selectedStory={selectedStory}
          onClose={() => {
            setShowStoryModal(false);
            dispatch(clearStoryData());
          }}
          onContinue={handleStoryStart}
          audioUri={selectedStory.audio}
        />
      )}

      {showBackpackModal && (
        <MagicBackpackModal
          onClose={() => setShowBackpackModal(false)}
          onVideoPress={() => handleBackpackContentPress("Video")}
          onAudioPress={() => handleBackpackContentPress("Audio")}
        />
      )}
    </div>
  );
};

export default WebMyJourneyScreen;
